import { useState, type ReactNode } from 'react';
import { CARD_CATALOG, type CatalogEntry } from '../../shared/cards';
import { COLORS } from '../../shared/constants';
import { CARD_GUIDE } from '../../shared/cardGuide';
import { Contours } from '../ui/Contours';
import { CardIcon } from '../round/CardIcon';
import { CardDemo } from '../round/CardDemo';
import type { CardFamily } from '../../shared/protocol';

// ============================================================
// v7 — How to Play. A read-only reference page: roles, the round loop,
// and an EXHAUSTIVE card codex generated from CARD_CATALOG × cardGuide
// (so it can never go stale). Entirely client-side, synthetic data only
// — it never reads live game state, so the privacy invariant is
// untouched and it renders without a SocketProvider (full spec §1/§2).
// ============================================================

const NAV = [
  { id: 'ht-overview', label: 'Overview' },
  { id: 'ht-roles', label: 'Roles' },
  { id: 'ht-round', label: 'The round' },
  { id: 'ht-hider', label: 'Hider cards' },
  { id: 'ht-seeker', label: 'Seeker cards' },
  { id: 'ht-scoring', label: 'Scoring' },
] as const;

const SEEKER_FAMILIES: { family: CardFamily; title: string; intro: string }[] = [
  { family: 'radar', title: 'Radius: “yes/no within a ring”', intro: 'Instant. Centers a circle on you; you learn inside or outside, and the zone shrinks to match. A smaller ring is more precise and costs more.' },
  // v11 §2K: this family holds the instant region cuts — Direction's diagonal
  // X-wedges, Quadrants' N/S–E/W cross, and Borders' band-vs-core match.
  { family: 'direction', title: 'Direction & region cuts: “which part of the map?”', intro: 'Instant. Direction splits the map into four DIAGONAL-edged wedges (an X centered on you); Quadrants uses the north-south/east-west cross instead; Borders asks outer band or inner core. You learn which region holds the hider and the zone shrinks to it.' },
  { family: 'thermometer', title: 'Slice: “walk, then cut along your path”', intro: 'Starts a timed walk. Move at least the card’s distance from where you started; the map is cut along the line you walked and the zone shrinks to the hider’s side. Walk too little and it fails.' },
  { family: 'photo', title: 'Photo: “make the hider prove it”', intro: 'Gives the hider limited time to upload a photo of the named subject (stripped of location data, shared to both teams). A clue, not a position. Miss the deadline and a penalty zone is revealed around the hider.' },
];

function seekerFamily(id: string): CardFamily {
  if (id.startsWith('radar')) return 'radar';
  if (id.startsWith('thermo')) return 'thermometer';
  if (id.startsWith('photo')) return 'photo';
  return 'direction';
}

/** Brand glyph for a card: the topographic CardIcon for seeker families, a small
 *  kind mark for hider cards (which have no CardFamily). */
function CardGlyph({ entry }: { entry: CatalogEntry }) {
  if (entry.deck === 'seeker') return <CardIcon family={seekerFamily(entry.id)} id={entry.id} size={30} />; // v15 BOLT-055: per-card marks
  const SIG = '#e8451b';
  const BONE = '#f5f1e6';
  const PORTAL = COLORS.portal;
  const id = entry.id;
  if (id.startsWith('bonus'))
    return (
      <svg width="30" height="30" viewBox="0 0 100 100" fill="none" aria-hidden="true">
        <circle cx="50" cy="50" r="34" stroke={BONE} strokeWidth="1.6" opacity="0.3" />
        <path d="M50 32 V68 M32 50 H68" stroke={SIG} strokeWidth="6" strokeLinecap="round" />
      </svg>
    );
  if (id === 'veto')
    return (
      <svg width="30" height="30" viewBox="0 0 100 100" fill="none" aria-hidden="true">
        <circle cx="50" cy="50" r="32" stroke={SIG} strokeWidth="6" />
        <line x1="28" y1="28" x2="72" y2="72" stroke={SIG} strokeWidth="6" strokeLinecap="round" />
      </svg>
    );
  if (id === 'portal')
    return (
      <svg width="30" height="30" viewBox="0 0 100 100" fill="none" aria-hidden="true">
        <circle cx="50" cy="50" r="30" stroke={PORTAL} strokeWidth="3" />
        <circle cx="50" cy="50" r="18" stroke={PORTAL} strokeWidth="3" opacity="0.7" />
        <circle cx="50" cy="50" r="6" fill={PORTAL} />
      </svg>
    );
  // curse (holdup / snail) — a stop octagon
  return (
    <svg width="30" height="30" viewBox="0 0 100 100" fill="none" aria-hidden="true">
      <path d="M35 14 H65 L86 35 V65 L65 86 H35 L14 65 V35 Z" fill={SIG} fillOpacity="0.18" stroke={SIG} strokeWidth="4" />
      <line x1="34" y1="50" x2="66" y2="50" stroke={SIG} strokeWidth="6" strokeLinecap="round" />
    </svg>
  );
}

function Chip({ text }: { text: string }) {
  return <span className="codex-chip">{text}</span>;
}

function CodexRow({ entry }: { entry: CatalogEntry }) {
  const [open, setOpen] = useState(false);
  const guide = CARD_GUIDE[entry.id]; // may be absent → catalog-description fallback
  return (
    <div className="codex-row" data-testid={`codex-${entry.id}`} data-card={entry.id} data-open={open}>
      <button
        className="codex-row__head"
        aria-expanded={open}
        data-testid={`codex-toggle-${entry.id}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="codex-row__icon">
          <CardGlyph entry={entry} />
        </span>
        <span className="codex-row__text">
          <span className="codex-row__name">{entry.label}</span>
          <span className="codex-row__desc">{entry.description}</span>
        </span>
        <span className="codex-row__chev" aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
      </button>
      {open && (
        <div className="codex-row__body" data-testid={`codex-body-${entry.id}`}>
          <p className="codex-row__how">{guide?.how ?? entry.description}</p>
          {guide && (
            <div className="codex-row__chips">
              {guide.mechanics.cost && <Chip text={`Cost: ${guide.mechanics.cost}`} />}
              {guide.mechanics.window && <Chip text={guide.mechanics.window} />}
              {guide.mechanics.duration && <Chip text={guide.mechanics.duration} />}
              <Chip text={guide.mechanics.mapImpact} />
            </div>
          )}
          {guide && <CardDemo demo={guide.demo} />}
        </div>
      )}
    </div>
  );
}

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} className="ht-section">
      <h2 className="ht-section__title">{title}</h2>
      {children}
    </section>
  );
}

export function HowToPlay({ onClose }: { onClose: () => void }) {
  // v14 BOLT-048: ?embed=1 (the iOS in-app WebView) — the sheet supplies the
  // close chrome, so the page hides its own Close/Done.
  const embedded = typeof location !== 'undefined' && new URLSearchParams(location.search).has('embed');
  const hiderCards = CARD_CATALOG.filter((c) => c.deck === 'hider');
  const goto = (id: string) => {
    if (typeof document !== 'undefined') document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="screen howto" data-testid="howto">
      <Contours opacity={0.08} />
      <div className="screen__inner">
        <div className="howto__top">
          <div>
            <div className="eyebrow">Field manual</div>
            <h1 className="howto__title">
              How to <em>play.</em>
            </h1>
          </div>
          {!embedded && (
            <button className="btn btn--ghost howto__close" data-testid="howto-close" onClick={onClose} aria-label="Close how to play">
              Close
            </button>
          )}
        </div>

        <nav className="howto__nav" data-testid="howto-nav">
          {NAV.map((n) => (
            <button key={n.id} className="howto__navchip" onClick={() => goto(n.id)}>
              {n.label}
            </button>
          ))}
        </nav>

        {/* v22 BOLT-112: lead with WHAT it is and the card loop, in plain
            language, before any per-card detail. Jake's field-tested way of
            explaining Bolt to someone who has never seen it. */}
        <Section id="ht-overview" title="Overview">
          <p className="ht-prose">
            Bolt is hide &amp; seek for the real world, played in person with friends and family. Two teams: <strong>Hiders</strong> and{' '}
            <strong>Seekers</strong>. The Seekers race to find the Hiders as fast as they can.
          </p>
          <p className="ht-prose">
            Both teams hold cards. Seekers play them to shrink the map and pull clues toward the hiding spot; Hiders play them to
            slow the Seekers down. It is quick and strategic: fast thinking and smart calls win.
          </p>
          <p className="ht-prose">
            The shaded <em>possible area</em> is the heart of it: the region where the hider could still be. Every seeker card
            narrows it; every card they play hands the hider more cards to fight back with.
          </p>
        </Section>

        <Section id="ht-roles" title="Roles">
          <p className="ht-prose">
            <strong>Hider:</strong> bolt to a hiding spot, lock it, and run the clock. You see the seeker’s pin and play hider cards to
            bank time and play cards against the seekers.
          </p>
          <p className="ht-prose">
            <strong>Seekers:</strong> play cards to shrink the possible area and close in, then tag the hider in person.
          </p>
          <p className="ht-prose ht-prose--note">
            <strong>The privacy promise:</strong> the seekers’ devices <em>never</em> receive the hider’s exact location. They only
            get the shrinking possible area and the clues they earn.
          </p>
        </Section>

        <Section id="ht-round" title="The round loop">
          <p className="ht-prose">
            A head start lets the hider bolt and hide. Then the seekers play cards. Here is the economy that ties the two decks
            together: <strong>every seeker card shrinks the zone, and every card the seekers play deals the hider new cards</strong>. A
            card’s cost is how many the hider draws when it resolves. The hider spends those cards to bank time and hit back. Play
            more, learn more, but hand the hider more ammunition.
          </p>
        </Section>

        <Section id="ht-hider" title="Hider cards">
          <div className="codex">
            {hiderCards.map((c) => (
              <CodexRow key={c.id} entry={c} />
            ))}
          </div>
        </Section>

        <Section id="ht-seeker" title="Seeker cards">
          <p className="ht-prose">
            Every seeker card <strong>shrinks the possible area</strong>. Each card’s cost is how many cards it deals into the hider’s
            hand when it resolves.
          </p>
          {SEEKER_FAMILIES.map((f) => {
            const cards = CARD_CATALOG.filter((c) => c.deck === 'seeker' && seekerFamily(c.id) === f.family);
            return (
              <div key={f.family} className="codex-family" data-family={f.family}>
                <h3 className="codex-family__title">{f.title}</h3>
                <p className="ht-prose ht-prose--small">{f.intro}</p>
                <div className="codex">
                  {cards.map((c) => (
                    <CodexRow key={c.id} entry={c} />
                  ))}
                </div>
              </div>
            );
          })}
        </Section>

        <Section id="ht-scoring" title="Scoring">
          <p className="ht-prose">
            Your score is the time you survive plus your banked bonuses, minus any penalties. In a series, both teams take turns
            hiding and the <strong>highest</strong> score wins. The host picks how it is tallied: best single round, cumulative, or
            most rounds won. Bonus cards are how the hider stockpiles time.
          </p>
        </Section>

        {!embedded && (
          <button className="btn btn--lg howto__done" data-testid="howto-done" onClick={onClose}>
            Back to the game
          </button>
        )}
      </div>
    </div>
  );
}
